import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  // Show the IP-notice modal once per fresh login.
  const [pendingIpNotice, setPendingIpNotice] = useState(() => {
    // JoinTeamPage and any other auth flow can set this flag right before a
    // hard-reload so the notice surfaces once on the next init.
    if (localStorage.getItem('cfp_show_ip_notice') === '1') {
      localStorage.removeItem('cfp_show_ip_notice');
      return true;
    }
    return false;
  });
  const acknowledgeIpNotice = () => setPendingIpNotice(false);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Heartbeat — pings backend every 60s so the admin can see active sessions
  // and measure how long users stay logged in. Fires once immediately, then
  // every minute while the tab is visible. Stops on logout/unmount.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const ping = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        await axios.post(`${API}/auth/heartbeat`);
      } catch (_e) {
        // Ignore — backend may be temporarily unavailable
      }
    };
    ping();
    const id = setInterval(ping, 60_000);
    // Send a best-effort logout when the user closes the tab so duration is recorded.
    // We use sendBeacon-style fetch with keepalive since axios won't flush during unload.
    const onUnload = () => {
      try {
        const url = `${API}/auth/logout`;
        fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      } catch (_e) { /* noop */ }
    };
    window.addEventListener('pagehide', onUnload);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchBusinesses();
      // Fetch dark mode preference
      try {
        const prefsRes = await axios.get(`${API}/user/preferences`);
        setDarkMode(prefsRes.data.dark_mode);
      } catch (e) {}
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const [businessesRes, activeRes] = await Promise.all([
        axios.get(`${API}/businesses`),
        axios.get(`${API}/businesses/active`).catch(() => ({ data: null }))
      ]);
      setBusinesses(businessesRes.data);
      // If the user previously picked the consolidated pseudo-business, keep it.
      const consolidatedPicked = localStorage.getItem('cf:consolidated-active') === '1';
      if (consolidatedPicked && (businessesRes.data?.length || 0) >= 2) {
        setActiveBusiness({
          id: '__consolidated__',
          name: `All businesses (${businessesRes.data.length})`,
          is_consolidated: true,
          role: 'viewer',  // consolidated view is read-only
        });
      } else if (activeRes.data) {
        setActiveBusiness(activeRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    }
  };

  const switchBusiness = async (businessId) => {
    try {
      if (businessId === '__consolidated__') {
        // Pseudo-business — no server call, just set the flag and reload
        localStorage.setItem('cf:consolidated-active', '1');
        await fetchBusinesses();
        window.location.reload();
        return;
      }
      localStorage.removeItem('cf:consolidated-active');
      await axios.put(`${API}/businesses/${businessId}/activate`);
      await fetchBusinesses();
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch business:', error);
      throw error;
    }
  };

  const createBusiness = async (name, logoBase64 = null) => {
    const response = await axios.post(`${API}/businesses`, { 
      name, 
      logo_base64: logoBase64 
    });
    await fetchBusinesses();
    return response.data;
  };

  const updateBusiness = async (businessId, data) => {
    const response = await axios.put(`${API}/businesses/${businessId}`, data);
    await fetchBusinesses();
    return response.data;
  };

  const toggleDarkMode = async () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    try {
      await axios.put(`${API}/user/preferences`, { dark_mode: newMode });
    } catch (e) {
      console.error('Failed to save dark mode preference');
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setPendingIpNotice(true);
    await fetchBusinesses();
    return userData;
  };

  const register = async (email, password, name, businessName, acceptedTerms) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      business_name: businessName,
      accepted_terms: acceptedTerms
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setPendingIpNotice(true);
    await fetchBusinesses();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setActiveBusiness(null);
    setBusinesses([]);
    setPendingIpNotice(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAuthenticated: !!user,
      activeBusiness,
      businesses,
      switchBusiness,
      createBusiness,
      updateBusiness,
      refreshBusinesses: fetchBusinesses,
      darkMode,
      toggleDarkMode,
      pendingIpNotice,
      acknowledgeIpNotice,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
